// Serviço de Autenticação Customizado - Zero Cookies
// Substitui Firebase Auth mantendo mesma interface

class CustomAuthService {
  constructor() {
    this.baseURL = import.meta.env.VITE_BACKEND_API_URL || 'http://127.0.0.1:5001';
    this.tokenKey = 'cbtkd_auth_token';
    this.userKey = 'cbtkd_user_data';
    this.refreshTokenKey = 'cbtkd_refresh_token';
    this.loginTimeKey = 'cbtkd_login_time'; // Chave para controlar tempo de login
    this.lastActivityKey = 'cbtkd_last_activity'; // Chave para última atividade
    this.longTaskModeKey = 'cbtkd_long_task_mode'; // Flag para modo de tarefa longa
    this.listeners = [];
    this.eventListeners = []; // Listeners para eventos específicos (longTaskMode, sessionRenewed, etc)
    this.warningNotified = false; // Flag para evitar múltiplos avisos
    this.activityTrackerStarted = false; // Flag para evitar múltiplos listeners
  }

  // Função para obter o timestamp de expiração da sessão (2 horas após login)
  getSessionExpiration(loginTime) {
    const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 horas em milissegundos
    return parseInt(loginTime) + SESSION_DURATION;
  }

  // Retorna usuário atual do localStorage
  getCurrentUser() {
    try {
      const userData = localStorage.getItem(this.userKey);
      const token = localStorage.getItem(this.tokenKey);
      const loginTime = localStorage.getItem(this.loginTimeKey);
      
      if (userData && token && loginTime) {
        // Iniciar rastreamento de atividade se ainda não foi iniciado
        if (!this.activityTrackerStarted) {
          this.startActivityTracking();
        }
        
        // Verificar se a sessão expirou (mais de 2 horas desde o login)
        if (this.isSessionExpired(loginTime)) {
          console.log('🔒 Sessão expirada (mais de 2 horas) - fazendo logout automático');
          this.signOut();
          return null;
        }
        
        // Verificar se o token JWT está expirado
        if (!this.isTokenExpired(token)) {
          return JSON.parse(userData);
        }
      }
      
      // Token expirado, tentar refresh (apenas se sessão não expirou)
      if (loginTime && !this.isSessionExpired(loginTime)) {
        return this.tryRefreshToken();
      }
      
      // Sessão expirada ou sem dados válidos
      this.signOut();
      return null;
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error);
      return null;
    }
  }

  // Verifica se token JWT está expirado
  isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  // Verifica se a sessão expirou (mais de 2 horas desde o login)
  isSessionExpired(loginTime) {
    try {
      const loginTimestamp = parseInt(loginTime);
      const currentTime = Date.now();
      const sessionExpiration = this.getSessionExpiration(loginTime);
      
      // Verificar se passou de 2 horas desde o login
      const hasExpired = currentTime >= sessionExpiration;
      
      if (hasExpired) {
        const loginDate = new Date(loginTimestamp);
        const currentDate = new Date(currentTime);
        console.log(`🕒 Sessão expirou: login em ${loginDate.toLocaleString()}, agora é ${currentDate.toLocaleString()} (duração: 2 horas)`);
      }
      
      return hasExpired;
    } catch {
      return true;
    }
  }

  // Verifica se a sessão está próxima de expirar (últimos 15 minutos antes de 2 horas)
  isSessionNearExpiry(loginTime) {
    try {
      const currentTime = Date.now();
      const sessionExpiration = this.getSessionExpiration(loginTime);
      const timeUntilExpiry = sessionExpiration - currentTime;
      const warningThreshold = 15 * 60 * 1000; // 15 minutos antes da expiração
      
      return timeUntilExpiry > 0 && timeUntilExpiry < warningThreshold;
    } catch {
      return false;
    }
  }

  // Obtém o tempo restante da sessão em minutos
  getSessionTimeRemaining(loginTime) {
    try {
      const currentTime = Date.now();
      const sessionExpiration = this.getSessionExpiration(loginTime);
      const timeRemaining = sessionExpiration - currentTime;
      
      return Math.max(0, Math.floor(timeRemaining / (1000 * 60))); // retorna em minutos
    } catch {
      return 0;
    }
  }

  // Registra atividade do usuário para renovar sessão
  updateActivity() {
    const now = Date.now().toString();
    localStorage.setItem(this.lastActivityKey, now);
    console.log('🔄 [SESSION] Atividade registrada - sessão renovada');
  }

  // Verifica se houve atividade recente (últimos 30 minutos)
  hasRecentActivity() {
    try {
      const lastActivity = localStorage.getItem(this.lastActivityKey);
      if (!lastActivity) return false;
      
      const activityTime = parseInt(lastActivity);
      const currentTime = Date.now();
      const timeSinceActivity = currentTime - activityTime;
      const ACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutos
      
      return timeSinceActivity < ACTIVITY_THRESHOLD;
    } catch {
      return false;
    }
  }

  // Renova a sessão se houver atividade recente OU se estiver em modo de tarefa longa
  renewSessionIfActive() {
    const loginTime = localStorage.getItem(this.loginTimeKey);
    if (!loginTime) return false;

    const isLongTaskMode = localStorage.getItem(this.longTaskModeKey) === 'true';
    const shouldRenew = isLongTaskMode || 
                        (this.hasRecentActivity() && this.isSessionNearExpiry(loginTime));

    // Se está em modo de tarefa longa OU teve atividade recente e a sessão está próxima de expirar
    if (shouldRenew) {
      const newLoginTime = Date.now().toString();
      localStorage.setItem(this.loginTimeKey, newLoginTime);
      localStorage.setItem(this.lastActivityKey, newLoginTime);
      
      const newExpiration = this.getSessionExpiration(newLoginTime);
      const reason = isLongTaskMode ? 'modo tarefa longa' : 'atividade';
      console.log(`✅ [SESSION] Sessão renovada por ${reason} - nova expiração: ${new Date(newExpiration).toLocaleString()}`);
      
      // Resetar flag de aviso
      this.warningNotified = false;
      
      // Notificar listeners sobre renovação
      this.notifyListeners({ 
        type: 'sessionRenewed', 
        user: this.getCurrentUser(),
        reason: reason
      });
      
      return true;
    }
    
    return false;
  }

  // Inicia rastreamento de atividade do usuário
  startActivityTracking() {
    if (this.activityTrackerStarted) return;
    this.activityTrackerStarted = true;

    // Eventos que indicam atividade do usuário
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    let activityTimeout = null;
    const handleActivity = () => {
      // Debounce: só registra atividade a cada 5 minutos
      if (activityTimeout) return;
      
      this.updateActivity();
      
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
      }, 5 * 60 * 1000); // 5 minutos
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Verificar periodicamente se deve renovar sessão
    // Em modo de tarefa longa, verifica a cada 30 segundos (mais frequente)
    setInterval(() => {
      this.renewSessionIfActive();
    }, 30 * 1000); // Verificar a cada 30 segundos

    console.log('✅ [SESSION] Rastreamento de atividade iniciado');
  }

  // Ativa modo de tarefa longa (para processamentos que levam horas)
  enableLongTaskMode(taskName = 'Processamento') {
    localStorage.setItem(this.longTaskModeKey, 'true');
    this.updateActivity(); // Registra atividade imediatamente
    console.log(`🔄 [SESSION] Modo tarefa longa ATIVADO: ${taskName}`);
    
    // Notificar listeners
    this.notifyListeners({ 
      type: 'longTaskModeEnabled',
      taskName: taskName,
      user: this.getCurrentUser()
    });
  }

  // Desativa modo de tarefa longa
  disableLongTaskMode() {
    localStorage.removeItem(this.longTaskModeKey);
    console.log('✅ [SESSION] Modo tarefa longa DESATIVADO');
    
    // Notificar listeners
    this.notifyListeners({ 
      type: 'longTaskModeDisabled',
      user: this.getCurrentUser()
    });
  }

  // Verifica se está em modo de tarefa longa
  isLongTaskMode() {
    return localStorage.getItem(this.longTaskModeKey) === 'true';
  }

  // Tenta renovar token usando refresh token
  async tryRefreshToken() {
    const refreshToken = localStorage.getItem(this.refreshTokenKey);
    const loginTime = localStorage.getItem(this.loginTimeKey);
    
    if (!refreshToken || !loginTime) return null;

    // Verificar se a sessão expirou (mais de 2 horas)
    if (this.isSessionExpired(loginTime)) {
      console.log('🔒 Não é possível renovar token - sessão expirou (mais de 2 horas)');
      this.signOut();
      return null;
    }

    try {
      const response = await fetch(`${this.baseURL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const { token, user, refreshToken: newRefreshToken } = await response.json();
        
        // Armazenar novos tokens (manter o mesmo loginTime)
        localStorage.setItem(this.tokenKey, token);
        localStorage.setItem(this.userKey, JSON.stringify(user));
        localStorage.setItem(this.refreshTokenKey, newRefreshToken);
        // NÃO atualizar loginTime - manter original para verificar duração da sessão
        
        return user;
      }
    } catch (error) {
      console.error('Erro ao renovar token:', error);
    }

    // Se falhou, limpar dados
    this.signOut();
    return null;
  }

  // Login com email e senha
  async signInWithEmailAndPassword(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro na autenticação');
      }

      const { token, user, refreshToken } = await response.json();
      
      // Armazenar no localStorage (não em cookies!) incluindo timestamp de login
      const loginTime = Date.now().toString();
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userKey, JSON.stringify(user));
      localStorage.setItem(this.refreshTokenKey, refreshToken);
      localStorage.setItem(this.loginTimeKey, loginTime);
      localStorage.setItem(this.lastActivityKey, loginTime); // Registrar atividade inicial
      
      const sessionExpiration = this.getSessionExpiration(loginTime);
      console.log(`✅ Login realizado - sessão válida por 2 horas até: ${new Date(sessionExpiration).toLocaleString()}`);
      
      // Iniciar rastreamento de atividade
      this.startActivityTracking();
      
      // Notificar listeners
      this.notifyListeners({ type: 'signIn', user });
      
      return { user };
    } catch (error) {
      console.error('Erro no login:', error);
      throw error;
    }
  }

  // Login com Google (substituir Firebase Auth)
  async signInWithPopup(provider) {
    // Implementar Google OAuth sem Firebase
    // Usar biblioteca google-auth-library ou similar
    // Processar callback e criar sessão local
    
    try {
      // Abrir popup do Google OAuth
      const authWindow = window.open(
        `${this.baseURL}/api/auth/google`, 
        'google-auth',
        'width=500,height=600'
      );

      // Aguardar callback
      return new Promise((resolve, reject) => {
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            
            // Verificar se login foi bem sucedido
            const user = this.getCurrentUser();
            if (user) {
              resolve({ user });
            } else {
              reject(new Error('Login cancelado'));
            }
          }
        }, 1000);
      });
    } catch (error) {
      console.error('Erro no login com Google:', error);
      throw error;
    }
  }

  // Logout
  async signOut() {
    try {
      const token = localStorage.getItem(this.tokenKey);
      
      // Invalidar token no backend
      if (token) {
        await fetch(`${this.baseURL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }).catch(() => {}); // Ignorar erros de rede
      }
    } finally {
      // Limpar localStorage
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.userKey);
      localStorage.removeItem(this.refreshTokenKey);
      localStorage.removeItem(this.loginTimeKey);
      localStorage.removeItem(this.lastActivityKey); // Limpar também atividade
      localStorage.removeItem(this.longTaskModeKey); // Limpar modo tarefa longa
      
      console.log('🔓 Logout realizado - dados de sessão removidos');
      
      // Notificar listeners
      this.notifyListeners({ type: 'signOut', user: null });
    }
  }

  // Obter token de acesso atual
  async getIdToken() {
    const token = localStorage.getItem(this.tokenKey);
    const loginTime = localStorage.getItem(this.loginTimeKey);
    
    if (!token || !loginTime) return null;
    
    // Verificar se a sessão expirou (mais de 2 horas)
    if (this.isSessionExpired(loginTime)) {
      console.log('🔒 Sessão expirou (mais de 2 horas) - fazendo logout automático');
      this.signOut();
      return null;
    }
    
    if (this.isTokenExpired(token)) {
      const user = await this.tryRefreshToken();
      if (!user) return null;
      return localStorage.getItem(this.tokenKey);
    }
    
    return token;
  }

  // Registrar listener para mudanças de autenticação
  onAuthStateChanged(callback) {
    this.listeners.push(callback);
    
    // Chamar imediatamente com estado atual
    const user = this.getCurrentUser();
    setTimeout(() => callback(user), 0);
    
    // Retornar função de cleanup
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notificar todos os listeners
  notifyListeners(event) {
    // Notificar listeners de auth (compatibilidade com Firebase Auth)
    this.listeners.forEach(listener => {
      try {
        listener(event.user);
      } catch (error) {
        console.error('Erro em listener de auth:', error);
      }
    });

    // Notificar listeners de eventos específicos
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Erro em event listener:', error);
      }
    });
  }

  // Adicionar listener para eventos específicos (longTaskMode, sessionRenewed, etc)
  addEventListener(callback) {
    this.eventListeners.push(callback);
    
    // Retornar função de cleanup
    return () => {
      this.eventListeners = this.eventListeners.filter(listener => listener !== callback);
    };
  }

  // Criar usuário (registro)
  async createUserWithEmailAndPassword(email, password, displayName) {
    try {
      const response = await fetch(`${this.baseURL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, displayName })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro no registro');
      }

      const { token, user, refreshToken } = await response.json();
      
      // Armazenar no localStorage incluindo timestamp de registro
      const loginTime = Date.now().toString();
      localStorage.setItem(this.tokenKey, token);
      localStorage.setItem(this.userKey, JSON.stringify(user));
      localStorage.setItem(this.refreshTokenKey, refreshToken);
      localStorage.setItem(this.loginTimeKey, loginTime);
      localStorage.setItem(this.lastActivityKey, loginTime); // Registrar atividade inicial
      
      const sessionExpiration = this.getSessionExpiration(loginTime);
      console.log(`✅ Registro realizado - sessão válida por 2 horas até: ${new Date(sessionExpiration).toLocaleString()}`);
      
      // Iniciar rastreamento de atividade
      this.startActivityTracking();
      
      // Notificar listeners
      this.notifyListeners({ type: 'signIn', user });
      
      return { user };
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  }

  // Reset de senha
  async sendPasswordResetEmail(email) {
    try {
      const response = await fetch(`${this.baseURL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao enviar email');
      }

      return true;
    } catch (error) {
      console.error('Erro no reset de senha:', error);
      throw error;
    }
  }

  // Verificar se usuário está logado
  isAuthenticated() {
    return this.getCurrentUser() !== null;
  }

  // Limpar dados expirados periodicamente
  startTokenCleanup() {
    setInterval(() => {
      const token = localStorage.getItem(this.tokenKey);
      const loginTime = localStorage.getItem(this.loginTimeKey);
      
      if (loginTime) {
        // Verificar se a sessão expirou (mais de 2 horas)
        if (this.isSessionExpired(loginTime)) {
          console.log('🔒 Limpeza automática: sessão expirou (mais de 2 horas)');
          this.signOut();
          return;
        }
        
        // Verificar se está próximo de expirar e mostrar aviso
        if (this.isSessionNearExpiry(loginTime) && !this.warningNotified) {
          const timeRemaining = this.getSessionTimeRemaining(loginTime);
          const hours = Math.floor(timeRemaining / 60);
          const minutes = timeRemaining % 60;
          
          console.warn(`⚠️ Sua sessão expirará em ${hours}h ${minutes}min. Salve seu trabalho!`);
          
          // Notificar listeners sobre expiração próxima
          this.notifyListeners({ 
            type: 'sessionWarning', 
            user: this.getCurrentUser(),
            timeRemaining: timeRemaining
          });
          
          this.warningNotified = true;
          
          // Reset do aviso após 30 minutos para mostrar novamente se necessário
          setTimeout(() => {
            this.warningNotified = false;
          }, 30 * 60 * 1000);
        }
      }
      
      if (token && this.isTokenExpired(token) && loginTime && !this.isSessionExpired(loginTime)) {
        this.tryRefreshToken();
      }
    }, 60000); // Verificar a cada minuto
  }
}

// Exportar instância singleton
const customAuth = new CustomAuthService();

// Iniciar limpeza automática de tokens
customAuth.startTokenCleanup();

export default customAuth;

// Também exportar com interface compatível com Firebase
export const auth = customAuth;
export const signInWithEmailAndPassword = customAuth.signInWithEmailAndPassword.bind(customAuth);
export const signInWithPopup = customAuth.signInWithPopup.bind(customAuth);
export const signOut = customAuth.signOut.bind(customAuth);
export const createUserWithEmailAndPassword = customAuth.createUserWithEmailAndPassword.bind(customAuth);
export const sendPasswordResetEmail = customAuth.sendPasswordResetEmail.bind(customAuth);
export const onAuthStateChanged = customAuth.onAuthStateChanged.bind(customAuth);